function sanitizeEnvUrl(value) {
  if (!value) {
    return "";
  }
  return value.replace(/^["']|["']$/g, "").trim();
}

function resolveDefaultBaseUrl() {
  if (typeof window === "undefined") {
    return sanitizeEnvUrl(process.env.REACT_APP_API_URL);
  }
  const isLocalEnvironment = window.location.hostname === "localhost";
  if (isLocalEnvironment) {
    return sanitizeEnvUrl(process.env.REACT_APP_API_URL_LOCAL || process.env.REACT_APP_API_URL);
  }
  return sanitizeEnvUrl(process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_LOCAL);
}

const DEFAULT_BASE_URL = resolveDefaultBaseUrl();
export { DEFAULT_BASE_URL };

function buildHeaders(token, includeJson = true) {
  const headers = {};
  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function parseJsonResponse(response, fallbackMessage) {
  if (!response.ok) {
    let message = fallbackMessage;
    try {
      const data = await response.json();
      message = data.error || data.message || fallbackMessage;
    } catch (_error) {
      message = fallbackMessage;
    }
    throw new Error(message);
  }
  return response.json();
}

export async function createReportJob(
  { startDate, endDate, templatePath, roles = [] },
  { baseUrl = DEFAULT_BASE_URL, token } = {},
) {
  const response = await fetch(`${baseUrl}/reports`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({
      startDate,
      endDate,
      templatePath,
      templates: [templatePath],
      roles,
    }),
  });

  return parseJsonResponse(response, "Failed to create report job");
}

export async function getReportJob(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/reports/${jobId}`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch report job");
}

export async function listMyReportJobs({ baseUrl = DEFAULT_BASE_URL, token, limit = 500 } = {}) {
  const response = await fetch(`${baseUrl}/reports?limit=${encodeURIComponent(limit)}`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch my report jobs");
}

export async function listStorageReportJobs({ baseUrl = DEFAULT_BASE_URL, token, limit = 500 } = {}) {
  const response = await fetch(`${baseUrl}/reports/storage?limit=${encodeURIComponent(limit)}`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch storage report jobs");
}

export async function getReportCapacity({ baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/reports/capacity`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch report capacity");
}

export async function listAdminReportJobs({ baseUrl = DEFAULT_BASE_URL, token, limit = 500 } = {}) {
  const response = await fetch(`${baseUrl}/admin/reports?limit=${encodeURIComponent(limit)}`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch admin report jobs");
}

export async function stopAdminReportJob(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/reports/${jobId}/stop`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({}),
  });

  return parseJsonResponse(response, "Failed to stop admin report job");
}

export async function downloadAdminReport(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/reports/${jobId}/download`, {
    method: "GET",
    headers: buildHeaders(token, false),
  });

  if (!response.ok) {
    let message = "Failed to download admin report";
    try {
      const data = await response.json();
      message = data.error || data.message || message;
    } catch (_error) {
      message = "Failed to download admin report";
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  return {
    blob,
    fileName: getDownloadFileName(response.headers.get("Content-Disposition")),
  };
}

export async function deleteAdminReportJob(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/reports/${jobId}`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to delete admin report job");
}

export async function resendAdminReportEmails(jobId, { baseUrl = DEFAULT_BASE_URL, token, recipients } = {}) {
  const response = await fetch(`${baseUrl}/admin/reports/${jobId}/resend-emails`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(recipients ? { recipients } : {}),
  });

  return parseJsonResponse(response, "Failed to resend report emails");
}

export async function cancelReportJob(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/reports/${jobId}/cancel`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({}),
  });

  return parseJsonResponse(response, "Failed to cancel report job");
}

export async function deleteReportJob(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/reports/${jobId}`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to delete report job");
}

export async function downloadReport(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/reports/${jobId}/download`, {
    method: "GET",
    headers: buildHeaders(token, false),
  });

  if (!response.ok) {
    let message = "Failed to download report";
    try {
      const data = await response.json();
      message = data.error || data.message || message;
    } catch (_error) {
      message = "Failed to download report";
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  return {
    blob,
    fileName: getDownloadFileName(response.headers.get("Content-Disposition")),
  };
}

export async function downloadStorageReport(jobId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/reports/storage/${jobId}/download`, {
    method: "GET",
    headers: buildHeaders(token, false),
  });

  if (!response.ok) {
    let message = "Failed to download storage report";
    try {
      const data = await response.json();
      message = data.error || data.message || message;
    } catch (_error) {
      message = "Failed to download storage report";
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  return {
    blob,
    fileName: getDownloadFileName(response.headers.get("Content-Disposition")),
  };
}

export function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function getDownloadFileName(contentDisposition) {
  if (!contentDisposition) {
    return null;
  }

  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || null;
}

// ── Clients management (admin) ───────────────────────────────────────

export async function listAdminClients({ baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/clients`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch clients");
}

export async function createAdminClient(data, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/clients`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(data),
  });

  return parseJsonResponse(response, "Failed to create client");
}

export async function updateAdminClient(clientId, data, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/clients/${clientId}`, {
    method: "PUT",
    headers: buildHeaders(token),
    body: JSON.stringify(data),
  });

  return parseJsonResponse(response, "Failed to update client");
}

export async function deleteAdminClient(clientId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/clients/${clientId}`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to delete client");
}

// ── Coordinator schedule management (admin) ──────────────────────────

export async function listCoordinatorClients({ baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/coordinator/clients`, {
    method: "GET",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to fetch coordinator clients");
}

export async function createCoordinatorClient(data, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/coordinator/clients`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(data),
  });

  return parseJsonResponse(response, "Failed to create coordinator client");
}

export async function updateCoordinatorClient(clientId, data, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/coordinator/clients/${clientId}`, {
    method: "PUT",
    headers: buildHeaders(token),
    body: JSON.stringify(data),
  });

  return parseJsonResponse(response, "Failed to update coordinator client");
}

export async function deleteCoordinatorClient(clientId, { baseUrl = DEFAULT_BASE_URL, token } = {}) {
  const response = await fetch(`${baseUrl}/admin/coordinator/clients/${clientId}`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, "Failed to delete coordinator client");
}